import {
	FormProvider,
	getFormProps,
	getInputProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { data, Form, Link } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import {
	CheckboxField,
	ErrorList,
	Field,
	TextareaField,
} from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import {
	applicationSources,
	applicationSourceLabels,
	ApplicationSourcesFieldSchema,
} from '#app/domain/sources.ts'
import { submitApplication } from '#app/utils/application.server.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { PII_CACHE_HEADERS, pipeHeaders } from '#app/utils/headers.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getTranslator } from '#app/utils/i18n.server.ts'
import { useTranslation, type Translator } from '#app/utils/i18n.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { getLinkedParticipantId } from '#app/utils/participant.server.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { type Route } from './+types/index.ts'
import { processApplications } from './process-applications.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

// schema factory: zod messages resolve in the caller's locale — client passes
// `useTranslation()`, the action passes `getTranslator(request)`
const ApplyFormSchema = (t: Translator) =>
	z.object({
		name: z.string().min(1).max(200),
		email: z.string().email().max(200),
		phone: z.string().max(50).optional(),
		tgHandle: z.string().max(100).optional(),
		social: z.string().max(300).optional(),
		knowsChannels: z.boolean().optional(),
		comment: z.string().max(2000).optional(),
		// «Звідки про нас дізнались?» — ОПЦІЙНЕ (сабміт без вибору проходить);
		// невалідний ключ у сирому POST → zod-fail → 400. Не впливає на consent
		// (той скидається лише зміною вибору КУРСІВ). app/domain/sources.ts SSOT.
		sources: ApplicationSourcesFieldSchema,
		// мультивибір потоків: N обраних курсів → N окремих Application (модель
		// незмінна). мінімум 1 обов'язково; валідність кожного id проти
		// openCourses — class-wide refine у дії (server-only, як і раніше).
		// Канонікалізація до УНІКАЛЬНИХ id на межі валідації (dedup ДО min-refine
		// і циклу подачі): крафтнутий POST з [A,A] інакше пройшов би валідацію і
		// поклався б лише на idempotency-вікно — тут дублі знімаються явно, тож
		// рівно одна анкета на курс незалежно від форми запиту.
		courseIds: z
			.array(z.string())
			.min(1, { message: t('error.apply.courseRequired') })
			.transform((ids) => Array.from(new Set(ids))),
		consentAccepted: z.boolean({
			required_error: t('error.apply.consentRequired'),
		}),
	})

type ApplyFormValues = z.infer<ReturnType<typeof ApplyFormSchema>>

async function getOpenCourses() {
	return prisma.course.findMany({
		select: { id: true, name: true, fop: true },
		where: { status: { in: ['upcoming', 'ongoing'] } },
		orderBy: { createdAt: 'desc' },
	})
}

/**
 * Prefill for the authenticated user's own data ONLY (spec § 3.1 «ідентифікація
 * по email + автозаповнення з бази», privacy-звуження: the verified email of the
 * session user — proven by the email → code login (auth-rework-note § 1.1) —
 * never a typed-in email; anonymous requests get null so no participant data
 * ever reaches an unauthenticated response).
 */
async function getPrefill(userId: string | null) {
	if (!userId) return null
	const participantId = await getLinkedParticipantId(userId)
	if (participantId) {
		const participant = await prisma.participant.findUniqueOrThrow({
			select: {
				name: true,
				email: true,
				phone: true,
				tgHandle: true,
				social: true,
			},
			where: { id: participantId },
		})
		return {
			name: participant.name,
			email: participant.email ?? undefined,
			phone: participant.phone ?? undefined,
			tgHandle: participant.tgHandle ?? undefined,
			social: participant.social ?? undefined,
		}
	}
	// logged-in first-timer: name/email from the User record
	const user = await prisma.user.findUniqueOrThrow({
		select: { name: true, email: true },
		where: { id: userId },
	})
	return { name: user.name ?? undefined, email: user.email }
}

export async function loader({ request }: Route.LoaderArgs) {
	const courses = await getOpenCourses()
	// без відкритих курсів форма не рендериться — PII не читаємо і не
	// серіалізуємо (не чіпає й write-on-GET лінкера)
	const prefill = courses.length
		? await getPrefill(await getUserId(request))
		: null
	return data(
		{ courses, prefill },
		// публічний URL із session-залежним PII — тримати поза кешами
		{ headers: PII_CACHE_HEADERS },
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

export async function action({ request }: Route.ActionArgs) {
	const t = getTranslator(request)
	const formData = await request.formData()
	await checkHoneypot(formData)
	const openCourses = await getOpenCourses()
	const submission = parseWithZod(formData, {
		// class-wide refine на масив: КОЖЕН обраний id має бути серед openCourses
		// (будь-який закритий/невалідний → вся подача відхиляється, 0 анкет —
		// AC-6). Runtime-провал уже валідного курсу обробляється нижче per-курс.
		schema: ApplyFormSchema(t).refine(
			(value) =>
				value.courseIds.every((id) =>
					openCourses.some((course) => course.id === id),
				),
			{ path: ['courseIds'], message: t('error.apply.courseUnavailable') },
		),
	})
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	// цикл по обраних курсах → існуючий submitApplication на кожен, винесений у
	// тестовану processApplications (обробка НЕЗАЛЕЖНА per-курс, не одна
	// транзакція: провал одного НЕ відкочує інші; idempotency-вікно (email+course)
	// + P2002-retry діють per-курс, тож повторний сабміт після часткового провалу
	// безпечний — дублі скіпаються). Проковтнутий раніше exception тепер
	// структуровано логується всередині processApplications.
	const { courseIds, ...applicant } = submission.value
	const { failedCourseIds } = await processApplications(courseIds, (courseId) =>
		submitApplication({ ...applicant, courseId }),
	)

	if (failedCourseIds.length) {
		const failedNames = failedCourseIds.map(
			(id) => openCourses.find((course) => course.id === id)?.name ?? id,
		)
		return data(
			{
				result: submission.reply({
					formErrors: [
						t('error.apply.partialFailure', {
							courses: failedNames.join(', '),
						}),
					],
				}),
			},
			{ status: 500 },
		)
	}

	return redirectWithToast('/apply/done', {
		type: 'success',
		title: t('toast.apply.title'),
		description: t('toast.apply.description'),
	})
}

export default function ApplyPage({
	loaderData,
	actionData,
}: Route.ComponentProps) {
	const t = useTranslation()
	const isPending = useIsPending()
	const { courses, prefill } = loaderData

	const [form, fields] = useForm<ApplyFormValues>({
		id: 'apply-form',
		constraint: getZodConstraint(ApplyFormSchema(t)),
		lastResult: actionData?.result,
		defaultValue: prefill ?? undefined,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ApplyFormSchema(t) })
		},
		shouldRevalidate: 'onBlur',
	})

	// клієнтський стан обраних курсів (conform трекає масив-поле реактивно;
	// FormValue<string[]> = string | string[] | undefined — нормалізуємо)
	const selectedCourseIds = Array.isArray(fields.courseIds.value)
		? fields.courseIds.value
		: fields.courseIds.value != null
			? [fields.courseIds.value]
			: []
	// consent-лейбл перелічує УНІКАЛЬНІ ФОПи обраних курсів; порожній вибір або
	// курси без ФОП → базовий лейбл без ФОП
	const selectedFops = Array.from(
		new Set(
			courses
				.filter((course) => selectedCourseIds.includes(course.id))
				.map((course) => course.fop)
				.filter((fop): fop is string => Boolean(fop)),
		),
	)
	const consentLabel = selectedFops.length
		? t('apply.consent', { fop: selectedFops.join(', ') })
		: t('apply.consentNoFop')

	return (
		<main className="container flex min-h-full flex-col justify-center py-16">
			<div className="mx-auto w-full max-w-lg">
				<div className="flex flex-col gap-3 text-center">
					<h1 className="text-h1">{t('apply.title')}</h1>
					<p className="text-body-md text-muted-foreground">
						{t('apply.subtitle')}
					</p>
				</div>
				{courses.length === 0 ? (
					<p className="text-muted-foreground pt-10 text-center">
						{t('apply.noCourses')}
					</p>
				) : (
					<FormProvider context={form.context}>
						{prefill ? (
							<p className="text-body-sm text-muted-foreground pt-6 text-center">
								{t('apply.prefillNote')}
							</p>
						) : (
							<p className="text-body-sm text-muted-foreground pt-6 text-center">
								{t('apply.loginHint')}{' '}
								<Link
									to="/login?redirectTo=/apply"
									className="text-foreground underline"
								>
									{t('apply.loginHintLink')}
								</Link>
							</p>
						)}
						<Form method="POST" className="pt-8" {...getFormProps(form)}>
							<HoneypotInputs />
							<fieldset
								aria-describedby={
									fields.courseIds.errors ? fields.courseIds.errorId : undefined
								}
							>
								<legend className="text-body-sm font-medium">
									{t('apply.courses')}
								</legend>
								<div className="flex flex-col gap-2 pt-2">
									{courses.map((course) => {
										const { key, ...checkboxProps } = getInputProps(
											fields.courseIds,
											{ type: 'checkbox', value: course.id },
										)
										// власний id на чекбокс: getInputProps віддає спільний id
										// масив-поля усім чекбоксам групи (HTML-колізія id)
										const checkboxId = `${fields.courseIds.id}-${course.id}`
										return (
											<div key={course.id} className="flex items-center gap-2">
												<input
													{...checkboxProps}
													key={key}
													id={checkboxId}
													// aria-label — label-wrap труїть accessible name
													// (house pattern); getByLabel(course.name) матчить
													aria-label={course.name}
													className="border-input h-4 w-4 rounded border"
													onChange={() => {
														// data-processing consent: показаний ФОП-перелік у
														// лейблі згоди РЕАКТИВНИЙ до набору курсів, тож
														// будь-яка зміна вибору скидає галочку (reset до
														// дефолту = unchecked) — акт згоди завжди
														// перепідтверджується проти ПОТОЧНОГО ФОП-тексту
														// (інакше згода, дана під один перелік ФОПів, мовчки
														// покрила б інший). Серверний снапшот згоди на момент
														// сабміту свідомо відкладено (defer-with-log).
														form.reset({ name: fields.consentAccepted.name })
													}}
												/>
												<label htmlFor={checkboxId} className="text-body-sm">
													{course.name}
												</label>
											</div>
										)
									})}
								</div>
								<div className="min-h-[32px] px-4 pt-1 pb-3">
									{fields.courseIds.errors ? (
										<ErrorList
											id={fields.courseIds.errorId}
											errors={fields.courseIds.errors}
										/>
									) : null}
								</div>
							</fieldset>
							<Field
								labelProps={{ children: t('participant.name') }}
								inputProps={{
									autoFocus: true,
									...getInputProps(fields.name, { type: 'text' }),
									autoComplete: 'name',
								}}
								errors={fields.name.errors}
							/>
							<div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
								<Field
									labelProps={{ children: t('participant.email') }}
									inputProps={{
										...getInputProps(fields.email, { type: 'email' }),
										autoComplete: 'email',
									}}
									errors={fields.email.errors}
								/>
								<Field
									labelProps={{ children: t('participant.phone') }}
									inputProps={{
										...getInputProps(fields.phone, { type: 'tel' }),
										autoComplete: 'tel',
									}}
									errors={fields.phone.errors}
								/>
								<Field
									labelProps={{ children: t('participant.tgHandle') }}
									inputProps={getInputProps(fields.tgHandle, { type: 'text' })}
									errors={fields.tgHandle.errors}
								/>
								<Field
									labelProps={{ children: t('participant.social') }}
									inputProps={getInputProps(fields.social, { type: 'text' })}
									errors={fields.social.errors}
								/>
							</div>
							<CheckboxField
								labelProps={{
									htmlFor: fields.knowsChannels.id,
									children: t('apply.knowsChannels'),
								}}
								buttonProps={getInputProps(fields.knowsChannels, {
									type: 'checkbox',
								})}
								errors={fields.knowsChannels.errors}
							/>
							<fieldset
								aria-describedby={
									fields.sources.errors ? fields.sources.errorId : undefined
								}
							>
								<legend className="text-body-sm font-medium">
									{t('apply.sources')}
								</legend>
								<div className="flex flex-col gap-2 pt-2">
									{applicationSources.map((source) => {
										const { key, ...checkboxProps } = getInputProps(
											fields.sources,
											{ type: 'checkbox', value: source },
										)
										// власний id на кожен чекбокс: getInputProps віддає спільний
										// id масив-поля усім чекбоксам групи (HTML-колізія id)
										const checkboxId = `${fields.sources.id}-${source}`
										const label = t(applicationSourceLabels[source])
										return (
											<div key={source} className="flex items-center gap-2">
												<input
													{...checkboxProps}
													key={key}
													id={checkboxId}
													// aria-label ГРУПОВО-КВАЛІФІКОВАНИЙ питанням: значення
													// «Telegram» інакше колізіонує з контактним полем
													// tgHandle (теж «Telegram») і підрядково зі згадкою в
													// knowsChannels — два контроли з однаковим accessible
													// name на одній формі. Кваліфікація лишає їх
													// унікальними (WCAG 2.5.3: видимий лейбл — підрядок
													// accessible name). БЕЗ consent-reset onChange: зміна
													// джерел НЕ чіпає згоду (reset лише від вибору КУРСІВ).
													aria-label={`${t('apply.sources')}: ${label}`}
													className="border-input h-4 w-4 rounded border"
												/>
												<label htmlFor={checkboxId} className="text-body-sm">
													{label}
												</label>
											</div>
										)
									})}
								</div>
								{fields.sources.errors ? (
									<div className="px-4 pt-1 pb-3">
										<ErrorList
											id={fields.sources.errorId}
											errors={fields.sources.errors}
										/>
									</div>
								) : null}
							</fieldset>
							<TextareaField
								labelProps={{ children: t('apply.comment') }}
								textareaProps={getTextareaProps(fields.comment)}
								errors={fields.comment.errors}
							/>
							<CheckboxField
								labelProps={{
									htmlFor: fields.consentAccepted.id,
									children: consentLabel,
								}}
								buttonProps={getInputProps(fields.consentAccepted, {
									type: 'checkbox',
								})}
								errors={fields.consentAccepted.errors}
							/>
							<ErrorList errors={form.errors} id={form.errorId} />
							<StatusButton
								className="w-full"
								type="submit"
								disabled={isPending}
								status={isPending ? 'pending' : 'idle'}
							>
								{t('apply.submit')}
							</StatusButton>
						</Form>
					</FormProvider>
				)}
			</div>
		</main>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
